/**
 * A* pathfinding within a single SurfaceGrid.
 * Finds optimal paths between cells using 8-directional movement.
 */
export default class GridPathfinder {
  // Maximum iterations to prevent blocking on very complex paths
  static MAX_ITERATIONS = 2000;

  /**
   * Create a grid pathfinder
   * @param {SurfaceGrid} grid - The surface grid to pathfind on
   */
  constructor(grid) {
    this.grid = grid;
  }

  /**
   * Find a path between two cells
   * @param {{x: number, z: number}} start - Start cell
   * @param {{x: number, z: number}} end - End cell
   * @returns {Array<{x: number, z: number}>|null} Array of cells or null if no path
   */
  findPath(start, end) {
    // Validate start and end
    if (!this.grid.isWalkable(start.x, start.z)) {
      // Try to find nearest walkable cell to start
      const nearStart = this.grid.findNearestWalkableCell(
        this.grid.minX + (start.x + 0.5) * this.grid.cellSize,
        this.grid.minZ + (start.z + 0.5) * this.grid.cellSize
      );
      if (!nearStart) return null;
      start = nearStart;
    }

    if (!this.grid.isWalkable(end.x, end.z)) {
      // Try to find nearest walkable cell to end
      const nearEnd = this.grid.findNearestWalkableCell(
        this.grid.minX + (end.x + 0.5) * this.grid.cellSize,
        this.grid.minZ + (end.z + 0.5) * this.grid.cellSize
      );
      if (!nearEnd) return null;
      end = nearEnd;
    }

    // Same cell - trivial path
    if (start.x === end.x && start.z === end.z) {
      return [{ x: start.x, z: start.z }];
    }

    // A* search
    const openSet = new BinaryHeap(item => item.f);
    const startKey = this._cellKey(start.x, start.z);
    const endKey = this._cellKey(end.x, end.z);

    const gScore = new Map();
    const fScore = new Map();
    const cameFrom = new Map();
    const closedSet = new Set();

    gScore.set(startKey, 0);
    fScore.set(startKey, this._heuristic(start, end));
    openSet.push({
      x: start.x,
      z: start.z,
      f: fScore.get(startKey)
    });

    let iterations = 0;
    while (openSet.size() > 0) {
      // Prevent blocking on very complex paths
      if (++iterations > GridPathfinder.MAX_ITERATIONS) {
        // Return best path found so far (to closest explored cell to goal)
        let bestCell = null;
        let bestDist = Infinity;
        for (const key of closedSet) {
          const [cx, cz] = key.split(',').map(Number);
          const dist = this._heuristic({ x: cx, z: cz }, end);
          if (dist < bestDist) {
            bestDist = dist;
            bestCell = { x: cx, z: cz };
          }
        }
        if (bestCell) {
          return this._reconstructPath(cameFrom, bestCell);
        }
        return null;
      }

      const current = openSet.pop();
      const currentKey = this._cellKey(current.x, current.z);

      // Reached goal
      if (currentKey === endKey) {
        return this._reconstructPath(cameFrom, current);
      }

      if (closedSet.has(currentKey)) continue;
      closedSet.add(currentKey);

      // Explore neighbors
      const neighbors = this.grid.getNeighbors(current.x, current.z);

      for (const neighbor of neighbors) {
        const neighborKey = this._cellKey(neighbor.x, neighbor.z);
        if (closedSet.has(neighborKey)) continue;

        const tentativeG = gScore.get(currentKey) + neighbor.cost;

        if (!gScore.has(neighborKey) || tentativeG < gScore.get(neighborKey)) {
          cameFrom.set(neighborKey, { x: current.x, z: current.z });
          gScore.set(neighborKey, tentativeG);
          const f = tentativeG + this._heuristic(neighbor, end);
          fScore.set(neighborKey, f);

          openSet.push({
            x: neighbor.x,
            z: neighbor.z,
            f: f
          });
        }
      }
    }

    // No path found
    return null;
  }

  /**
   * Octile distance heuristic for 8-directional grids
   * @private
   */
  _heuristic(from, to) {
    const dx = Math.abs(to.x - from.x);
    const dz = Math.abs(to.z - from.z);
    // Octile distance: diagonal movement costs sqrt(2)
    return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz);
  }

  /**
   * Generate a unique key for a cell
   * @private
   */
  _cellKey(x, z) {
    return `${x},${z}`;
  }

  /**
   * Reconstruct path from cameFrom map
   * @private
   */
  _reconstructPath(cameFrom, current) {
    const path = [{ x: current.x, z: current.z }];
    let currentKey = this._cellKey(current.x, current.z);

    while (cameFrom.has(currentKey)) {
      const prev = cameFrom.get(currentKey);
      path.unshift({ x: prev.x, z: prev.z });
      currentKey = this._cellKey(prev.x, prev.z);
    }

    return path;
  }

  /**
   * Find path and convert to world coordinates
   * @param {{x: number, z: number}} startWorld - Start position in world coords
   * @param {{x: number, z: number}} endWorld - End position in world coords
   * @returns {Array<{x: number, y: number, z: number}>|null} World coordinate path or null
   */
  findPathWorld(startWorld, endWorld) {
    const startCell = this.grid.worldToCell(startWorld.x, startWorld.z);
    const endCell = this.grid.worldToCell(endWorld.x, endWorld.z);

    const cellPath = this.findPath(startCell, endCell);
    if (!cellPath) return null;

    // Convert to world coordinates
    return cellPath.map(cell => this.grid.cellToWorld(cell.x, cell.z));
  }
}

/**
 * Binary heap for efficient priority queue operations
 * Used by A* for the open set
 */
class BinaryHeap {
  constructor(scoreFunction) {
    this.content = [];
    this.scoreFunction = scoreFunction;
  }

  push(element) {
    this.content.push(element);
    this._bubbleUp(this.content.length - 1);
  }

  pop() {
    const result = this.content[0];
    const end = this.content.pop();
    if (this.content.length > 0) {
      this.content[0] = end;
      this._sinkDown(0);
    }
    return result;
  }

  size() {
    return this.content.length;
  }

  _bubbleUp(n) {
    const element = this.content[n];
    const score = this.scoreFunction(element);

    while (n > 0) {
      const parentN = Math.floor((n - 1) / 2);
      const parent = this.content[parentN];

      if (score >= this.scoreFunction(parent)) break;

      this.content[parentN] = element;
      this.content[n] = parent;
      n = parentN;
    }
  }

  _sinkDown(n) {
    const length = this.content.length;
    const element = this.content[n];
    const score = this.scoreFunction(element);

    while (true) {
      const child2N = (n + 1) * 2;
      const child1N = child2N - 1;
      let swap = null;
      let child1Score;

      if (child1N < length) {
        const child1 = this.content[child1N];
        child1Score = this.scoreFunction(child1);
        if (child1Score < score) {
          swap = child1N;
        }
      }

      if (child2N < length) {
        const child2 = this.content[child2N];
        const child2Score = this.scoreFunction(child2);
        if (child2Score < (swap === null ? score : child1Score)) {
          swap = child2N;
        }
      }

      if (swap === null) break;

      this.content[n] = this.content[swap];
      this.content[swap] = element;
      n = swap;
    }
  }
}
